import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, Router } from "@angular/router";
import { AuthService } from "@abp/ng.core";
import { AuthCoordinationService } from "../../services/auth-coordination.service";
import { interval, of, EMPTY, Observable } from "rxjs";
import { takeWhile, switchMap, first } from "rxjs/operators";
import { BlockUiDialogComponent } from '../components/block-ui-dialog.component';
import { DialogService } from 'primeng/dynamicdialog';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {

  constructor(
    private authService: AuthService,
    private authCoordination: AuthCoordinationService,
    private dialogService: DialogService,
    private router: Router
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    // If already authenticated, allow access
    if (this.authService.isAuthenticated) {
      this.authCoordination.clearLoginFlag();
      return true;
    }

    // Handle login process and return Observable<boolean>
    return this.handleLoginObservable(state.url);
  }

  private handleLoginObservable(returnUrl: string): Observable<boolean> {
    // Check if another tab is already handling login
    if (this.authCoordination.isOtherTabLoggingIn()) {
      // Wait for other tab to complete login
      // Show BlockUI dialog to indicate redirecting to Azure AD login page
      this.dialogService.open(BlockUiDialogComponent, {
         
        header: 'Login In Progress',
        width: '480px',
        modal: true,
        closable: false,
        data: {
          message: 'Logging in, please wait...',
        },
      });
      return this.waitForOtherTabLoginObservable(returnUrl);
    } else {
      // Set login flag and proceed with login
      this.authCoordination.setLoginFlag();
      this.authService.navigateToLogin({ returnUrl });
      // Return false since we're redirecting to login
      return of(false);
    }
  }

  private waitForOtherTabLoginObservable(returnUrl: string): Observable<boolean> {
    // Get random wait time between 20-30 seconds
    const waitTime = this.authCoordination.getRandomWaitTime();
    const startTime = Date.now();

    // Check authentication status every second using interval
    return interval(1000).pipe(
      takeWhile(() => {
        const elapsed = Date.now() - startTime;
        // Continue checking if user is not authenticated and wait time hasn't elapsed

        return !this.authService.isAuthenticated && elapsed < waitTime;
      }, true), // Include the final emission
      switchMap(() => {
        const elapsed = Date.now() - startTime;

        // If user is authenticated, return true
        if (this.authService.isAuthenticated) {
          window.location.href = returnUrl;
          return this.router.navigateByUrl(returnUrl);
        }

        // If wait time has elapsed and still not authenticated, proceed with login
        if (elapsed >= waitTime) {
          this.authCoordination.setLoginFlag();
          this.authService.navigateToLogin({ returnUrl });
          return of(false);
        }

        // Continue waiting
        return EMPTY;
      }),
      first() // Take only the first emission (either true or false)
    );
  }
}
